/**
 * The materials editor's whole behaviour: what the trainer has typed, what the policy makes of it,
 * and whether it may be saved.
 *
 * A reducer plus one projection, and no React anywhere — which is what lets the four rules a
 * trainer actually meets (an `http` address, the same address twice, a label at the limit, a
 * `javascript:` payload) be exercised without rendering a field. `lib/useExerciseMaterials` hosts
 * it; `ui/MaterialsBlock` draws what `materialsEditorView` answers and decides nothing.
 *
 * ── The POLICY is the entity's, and this module does not restate a single rule of it ────────
 * `checkExerciseLinks` is `entities/exercise-library`'s mirror of the backend's `ExternalLinkPolicy`
 * (R12) — https only and never upgraded, no credentials, a label of at most 80, at most three
 * DISTINCT destinations with duplicates collapsed rather than refused. Everything here is about the
 * EDITOR: which rows exist, which one carries the refusal, and whether anything has changed.
 *
 * ── An entirely empty row is a placeholder, not a refusal ───────────────────────────────────
 * „Dodaj link" puts a blank pair of fields on screen, and the trainer has typed nothing into either
 * yet. Running it through the policy would answer `missingAddress` and paint a refusal under a
 * field nobody has touched — the editor would open shouting. So a row with a blank address AND a
 * blank label is dropped before the policy sees it, and dropped again on save.
 *
 * A row with a label and no address is a different thing: the trainer said what it is and not where
 * it goes, and `missingAddress` is exactly the right sentence for it.
 *
 * ── `dirty` compares what would be SUBMITTED, not what is typed ─────────────────────────────
 * Trimmed address and trimmed label against the baseline, in order. So trailing whitespace is not a
 * change, and an empty row added and left empty is not a change either — which matters because
 * „Anuluj" and the leave guard both read this, and a trainer who pressed „Dodaj link" and changed
 * their mind must not be asked whether they want to discard nothing.
 *
 * It deliberately does NOT compare through the policy's normalisation: two spellings of one
 * destination collapse on SAVE, and a trainer who has just replaced a link with a differently
 * spelled version of the same place has still edited the list they are looking at.
 */

import {
  MAX_EXERCISE_LINKS,
  checkExerciseLinks,
  type AcceptedLink,
  type LinkRefusal,
  type SubmittedLink,
} from '~/entities/exercise-library';

/**
 * One row of the editor.
 *
 * The `key` is the row's identity for React and for the reducer, and it is NOT the address: a
 * trainer editing a url character by character would otherwise re-key the field on every keystroke
 * and lose the caret, and two blank rows would share one key.
 */
export interface MaterialRowDraft {
  readonly key: string;
  readonly url: string;
  readonly label: string;
}

export interface MaterialsEditorState {
  /** The saved list this editor started from — what `dirty` is measured against and what `reset` returns to. */
  readonly baseline: readonly SubmittedLink[];
  readonly rows: readonly MaterialRowDraft[];
  /** The next row's key. A counter rather than a length, so a removal cannot make two rows agree. */
  readonly minted: number;
}

export type MaterialsEditorAction =
  /**
   * Open the editor on a saved list — the ONE action that carries data in.
   *
   * The links are the block's own, read off the catalogue at the moment the trainer entered, so the
   * baseline can never describe a list nobody is looking at any more. `withBlank` is what
   * „Dodaj link" means from read mode: enter, with the empty row already there.
   */
  | { readonly type: 'open'; readonly links: readonly SubmittedLink[]; readonly withBlank: boolean }
  | { readonly type: 'add' }
  | { readonly type: 'setUrl'; readonly key: string; readonly value: string }
  | { readonly type: 'setLabel'; readonly key: string; readonly value: string }
  | { readonly type: 'remove'; readonly key: string }
  | { readonly type: 'reset' };

function draftsOf(links: readonly SubmittedLink[], from: number): MaterialRowDraft[] {
  return links.map((link, index) => ({
    key: `link-${String(from + index)}`,
    url: link.url,
    label: link.label,
  }));
}

/**
 * An editor opened on a saved list. The baseline is that list, and the rows start as a copy of it.
 *
 * `from` is where the key counter picks up. Every reopen mints PAST everything this editor has
 * already handed out, so React is never given the key of a field the trainer was typing in a moment
 * ago — which would carry a caret and a selection across two different links.
 */
export function materialsEditorState(
  links: readonly SubmittedLink[],
  from = 0,
): MaterialsEditorState {
  return { baseline: links, rows: draftsOf(links, from), minted: from + links.length };
}

export function materialsEditorReducer(
  state: MaterialsEditorState,
  action: MaterialsEditorAction,
): MaterialsEditorState {
  switch (action.type) {
    case 'open': {
      const opened = materialsEditorState(action.links, state.minted);
      return action.withBlank ? materialsEditorReducer(opened, { type: 'add' }) : opened;
    }
    case 'add':
      // The cap is enforced by the POLICY on save and by `canAdd` on the control; a reducer that
      // also refused here would be the rule in two places, and they would drift.
      return {
        ...state,
        rows: [...state.rows, { key: `link-${String(state.minted)}`, url: '', label: '' }],
        minted: state.minted + 1,
      };
    case 'setUrl':
      return {
        ...state,
        rows: state.rows.map((row) => (row.key === action.key ? { ...row, url: action.value } : row)),
      };
    case 'setLabel':
      return {
        ...state,
        rows: state.rows.map((row) =>
          row.key === action.key ? { ...row, label: action.value } : row,
        ),
      };
    case 'remove':
      return { ...state, rows: state.rows.filter((row) => row.key !== action.key) };
    case 'reset':
      // Fresh keys, minted past everything this editor has already handed out: a reset that reused
      // `link-0` would hand React the key of a field the trainer had just been typing in.
      return {
        ...state,
        rows: draftsOf(state.baseline, state.minted),
        minted: state.minted + state.baseline.length,
      };
  }
}

/** One row as the editor draws it: what was typed, and what the policy made of it. */
export interface MaterialRowView {
  readonly draft: MaterialRowDraft;
  /** Why this row is refused, or `null`. At most ONE row carries a refusal — the policy stops at the first. */
  readonly refusal: LinkRefusal | null;
}

export interface MaterialsEditorView {
  readonly rows: readonly MaterialRowView[];
  /** Everything the save would send: trimmed, normalised, duplicates collapsed. */
  readonly accepted: readonly AcceptedLink[];
  /** Whether „Dodaj link" is offered — the cap counts ROWS, because that is what the trainer can see. */
  readonly canAdd: boolean;
  /** Whether anything would be written. See the header: trimmed, in order, against the baseline. */
  readonly dirty: boolean;
  /** Why the save is refused, or `null`. Exactly the refusal the one refused row carries. */
  readonly blocked: LinkRefusal | null;
}

/**
 * Why „Zapisz materiały" will not act, or `null` when it will.
 *
 * The button is `aria-disabled` rather than `disabled` — view ADR §12, so the reason under it stays
 * reachable — and it used to have no reason to point at: a trainer who pressed it met a control
 * that did nothing, said nothing and moved focus nowhere. Two things can stop it and they want
 * different acts from the trainer, so they are two answers rather than one boolean.
 *
 * A REFUSED row is named first, because it is the one the trainer can do something about: a list
 * with a bad address in it is also a list that has changed, so both conditions hold at once and
 * „nothing to save" would be the less useful of the two sentences.
 */
export type MaterialsSaveBlock =
  /** A row's address is refused. The block moves focus to that field on a refused press. */
  | 'refused'
  /** Nothing would be written. The trainer has not changed anything yet. */
  | 'unchanged';

export function materialsSaveBlock(view: MaterialsEditorView): MaterialsSaveBlock | null {
  if (view.blocked !== null) return 'refused';
  return view.dirty ? null : 'unchanged';
}

/** What is actually submitted: the blank placeholders dropped, everything else trimmed. */
function submittedRows(rows: readonly MaterialRowDraft[]): MaterialRowDraft[] {
  return rows.filter((row) => row.url.trim() !== '' || row.label.trim() !== '');
}

function sameAsBaseline(
  rows: readonly MaterialRowDraft[],
  baseline: readonly SubmittedLink[],
): boolean {
  if (rows.length !== baseline.length) return false;
  return rows.every((row, index) => {
    const saved = baseline[index];
    return (
      saved !== undefined && row.url.trim() === saved.url.trim() && row.label.trim() === saved.label.trim()
    );
  });
}

/** Everything a surface drawing this editor needs, from the state alone. */
export function materialsEditorView(state: MaterialsEditorState): MaterialsEditorView {
  const submitted = submittedRows(state.rows);
  const check = checkExerciseLinks(submitted.map((row) => ({ url: row.url, label: row.label })));

  // The policy names the index it refused at, and that index is into the SUBMITTED list — so the
  // refusal is put back on the row's own key rather than on its position among all the rows.
  const refusedKey = check.refused === null ? null : (submitted[check.refused.at]?.key ?? null);

  return {
    rows: state.rows.map((draft) => ({
      draft,
      refusal: draft.key === refusedKey ? (check.refused?.refusal ?? null) : null,
    })),
    accepted: check.accepted,
    canAdd: state.rows.length < MAX_EXERCISE_LINKS,
    dirty: !sameAsBaseline(submitted, state.baseline),
    blocked: check.refused?.refusal ?? null,
  };
}
